// Management > Dashboard — firm-wide operator/CEO view with financial +
// pipeline tiles.
//
// Operator-locked 2026-05-20: 6 pulse + 2 backlog tiles tailored to
// CEO/Operator role (pipeline value, revenue MTD, open AR, AP outstanding,
// active orders, new clients MTD). Replaces the firm-wide-mirror-of-Sales
// shape from 2026-05-18.
//
// Hits GET /management/dashboard. Service: apps/api/src/services/
// management-dashboard.ts.
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ClipboardList,
  DollarSign,
  Receipt,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
  Inbox,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  DashboardTiles,
  type TileFormat,
} from "@/components/DashboardTiles";

type ManagementPulseSlug =
  | "mgmt-firm-pipeline-value"
  | "mgmt-revenue-mtd"
  | "mgmt-open-ar"
  | "mgmt-ap-outstanding"
  | "mgmt-active-orders"
  | "mgmt-new-clients-mtd";

type ManagementBacklogSlug =
  | "mgmt-unreviewed-signups"
  | "mgmt-pending-project-requests";

interface ManagementDashboardResponse {
  pulse: Array<{
    slug: ManagementPulseSlug;
    label: string;
    value: number;
    format: TileFormat;
    href: string;
    description: string;
  }>;
  backlog: Array<{
    slug: ManagementBacklogSlug;
    label: string;
    value: number;
    format: TileFormat;
    href: string;
    description: string;
  }>;
  fetchedAt: string;
}

const PULSE_ICON: Record<ManagementPulseSlug, typeof DollarSign> = {
  "mgmt-firm-pipeline-value": DollarSign,
  "mgmt-revenue-mtd": TrendingUp,
  "mgmt-open-ar": Receipt,
  "mgmt-ap-outstanding": Wallet,
  "mgmt-active-orders": ClipboardList,
  "mgmt-new-clients-mtd": Users,
};

const BACKLOG_ICON: Record<ManagementBacklogSlug, typeof UserPlus> = {
  "mgmt-unreviewed-signups": UserPlus,
  "mgmt-pending-project-requests": Inbox,
};

export function ManagementDashboardPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["management", "dashboard"],
    queryFn: () => api<ManagementDashboardResponse>("/management/dashboard"),
    refetchOnWindowFocus: true,
  });

  return (
    <div className="mx-auto w-full">
      <div className="mb-10">
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Management · Dashboard
        </p>
        <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          The firm today.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-brown-400">
          Pipeline, revenue, AR + AP — firm-wide.{" "}
          <Link
            to="/command/dashboard"
            className="font-semibold text-ink hover:underline"
          >
            Command Center
          </Link>{" "}
          ·{" "}
          <Link
            to="/reports"
            className="font-semibold text-ink hover:underline"
          >
            Reports
          </Link>
        </p>
      </div>

      <DashboardTiles
        pulse={data?.pulse ?? []}
        backlog={data?.backlog ?? []}
        pulseIcons={PULSE_ICON}
        backlogIcons={BACKLOG_ICON}
        isLoading={isLoading}
        isError={isError}
        emptyBacklogCopy="Inbox zero across the firm. No triage queue."
      />
    </div>
  );
}
